/**
 * AI Spend — every model call the gateway made, and what it cost.
 *
 * Reads /api/admin/spend/*, which reads the `llm_calls` ledger. That is a
 * different source from the cost columns on the Providers and User Usage tabs,
 * which read `ai_cost_records` and are wrong: a bridge wrote the Compass stage
 * name where the model belonged, nothing matched a price, and every row took a
 * $3/$15 Claude Sonnet default. Those figures claimed $6.75 of chat spend
 * against a real $0.78.
 *
 * So this view is built around one rule: never render an unknown cost as $0.00.
 * A missing price is the reason the old numbers were believed for as long as
 * they were, and `$0.0000` is indistinguishable from free. Capture Integrity is
 * therefore the first thing on the page rather than a detail at the bottom —
 * every total below it is a sum over the calls it excludes, so it is the figure
 * that says whether the rest can be trusted.
 *
 * Non-primary share sits beside it. Failover is unconditional by design, on the
 * grounds that no request should die over money, which means a sustained outage
 * at Groq silently moves spend to a provider several times dearer. That trade
 * was made deliberately; this is what makes it visible.
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table'
import { Badge } from '@/components/ui/Badge'
import {
 DollarSign, ShieldAlert, GitBranch, RefreshCw, AlertCircle,
 Users, MessageSquare, Activity, ChevronRight, ArrowLeft,
} from 'lucide-react'
import { BRAND } from '../../config/brand'

// ── Types ─────────────────────────────────────────────────────
// Mirror src/llm_gateway/analytics.py. `micros` is integer micros of USD
// throughout, never a float: summing thirty layers of a turn as floats drifts,
// and the ledger stores micros for the same reason.

interface Measures {
 attempts: number
 calls: number
 ok: number
 failed: number
 skipped: number
 fallback_attempts: number
 unpriced: number
 untokened: number
 legacy: number
 micros: number
 prompt_tokens: number
 cached_prompt_tokens: number
 completion_tokens: number
 reasoning_tokens: number
 p50_ms: number
 p95_ms: number
 max_ms: number
}

interface Total extends Measures {
 capture: number
 non_primary_share: number
 micros_per_user: number
 micros_per_chat: number
 micros_per_call: number
 /** Distinct Compass turns in the window. */
 turns?: number
 /** Average spend of one yanamas AI turn (several gateway calls). */
 micros_per_turn?: number
}

interface Overview {
 window: string
 total: Total
 by_provider: Array<Measures & { provider: string }>
 by_class: Array<Measures & { model_class: string }>
 by_purpose: Array<Measures & { purpose: string }>
 by_model: Array<Measures & { model: string }>
 users: number
 chats: number
}

interface UserRow extends Measures {
 user_key: string
 saas_user_id: number | null
 chats: number
 micros_per_chat: number
}

interface ChatRow extends Measures {
 chat_id: string
 user_key: string | null
 turns: number
 micros_per_turn: number
 first_call_at: string | null
 last_call_at: string | null
}

interface CallRow {
 id: string
 call_id: string | null
 attempt_index: number | null
 request_ts: string | null
 latency_ms: number | null
 user_key: string | null
 chat_id: string | null
 turn_id: string | null
 purpose: string | null
 class_requested: string | null
 class_resolved: string | null
 provider: string | null
 model: string | null
 status: string | null
 http_status: number | null
 error_kind: string | null
 error_message: string | null
 skipped_reason: string | null
 via_gateway: boolean | null
 prompt_tokens: number | null
 cached_prompt_tokens: number | null
 completion_tokens: number | null
 reasoning_tokens: number | null
 cost_usd_micros: number | null
 priced: boolean | null
}

const WINDOWS = ['1h', '24h', '7d', '30d', '90d', 'all'] as const
type Window = typeof WINDOWS[number]

// ── Formatting ────────────────────────────────────────────────

/** Micros to dollars. Four decimals, because a Compass turn costs microdollars. */
const money = (micros: number, decimals = 4): string =>
 `$${(micros / 1_000_000).toFixed(decimals)}`

/**
 * Money for a row that may have unpriced calls in it.
 *
 * The whole point of the view. A row with 12 unpriced calls is not showing you
 * its cost, it is showing you a lower bound, and it says so rather than letting
 * the figure be read as complete.
 */
const moneyOf = (m: Measures, decimals = 4) =>
 m.unpriced > 0 ? (
  <span className="whitespace-nowrap" title={`${m.unpriced} of ${m.attempts} attempts have no known price`}>
   {money(m.micros, decimals)}
   <span className="text-warn ml-1">+?</span>
  </span>
 ) : (
  <span className="whitespace-nowrap">{money(m.micros, decimals)}</span>
 )

const num = (n: number): string => n.toLocaleString()

const pct = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`

const shortTs = (iso: string | null): string =>
 iso ? iso.replace('T', ' ').slice(5, 19) : '—'

// ── Fetch ─────────────────────────────────────────────────────

const get = async <T,>(path: string): Promise<T> => {
 const res = await fetch(path, { credentials: 'include' })
 const body = await res.json().catch(() => ({}))
 if (!res.ok || body?.success === false) {
  throw new Error(body?.error || `Request failed (${res.status})`)
 }
 return body as T
}

// ── Pool health ───────────────────────────────────────────────
// Mirrors src/llm_gateway/preflight.py.

interface PreflightEntry {
 ref: string
 ok: boolean
 priced: boolean
 latency_ms?: number
 error_kind?: string
 error?: string
}

interface PreflightClass {
 class: string
 entries: PreflightEntry[]
 serving: number
 ok: boolean
 has_spare: boolean
 primary_ok: boolean
}

interface Preflight {
 checked_at: string
 classes: PreflightClass[]
 dead_classes: string[]
 classes_without_spare: string[]
 classes_on_fallback: string[]
}

// ── Component ─────────────────────────────────────────────────

export function AiSpendTab() {
 const [window, setWindow] = useState<Window>('24h')
 const [overview, setOverview] = useState<Overview | null>(null)
 const [users, setUsers] = useState<UserRow[]>([])
 const [chats, setChats] = useState<ChatRow[]>([])
 const [calls, setCalls] = useState<CallRow[]>([])
 const [loading, setLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)

 // Drill-down: null means the whole window. Selecting a user narrows the chat
 // list; selecting a chat narrows the call list.
 const [userKey, setUserKey] = useState<string | null>(null)
 const [chatId, setChatId] = useState<string | null>(null)
 const [callFilter, setCallFilter] = useState<'' | 'failed' | 'fallback' | 'unpriced'>('')

 // Not loaded with the page: a sweep calls every model in every pool, so it
 // costs money and takes a few seconds. It is on a button for that reason.
 const [preflight, setPreflight] = useState<Preflight | null>(null)
 const [preflightBusy, setPreflightBusy] = useState(false)
 const [preflightError, setPreflightError] = useState<string | null>(null)

 const runPreflight = useCallback(async () => {
  setPreflightBusy(true)
  setPreflightError(null)
  try {
   const res = await fetch('/api/admin/llm/preflight', {
    method: 'POST',
    credentials: 'include',
   })
   const body = await res.json().catch(() => ({}))
   if (!res.ok || body?.success === false) {
    throw new Error(body?.error || `Request failed (${res.status})`)
   }
   setPreflight(body as Preflight)
  } catch (e) {
   setPreflightError(e instanceof Error ? e.message : String(e))
  } finally {
   setPreflightBusy(false)
  }
 }, [])

 const load = useCallback(async () => {
  setLoading(true)
  setError(null)
  try {
   const chatQuery = userKey ? `&user_key=${encodeURIComponent(userKey)}` : ''
   const callQuery = [
    chatId ? `&chat_id=${encodeURIComponent(chatId)}` : '',
    userKey ? `&user_key=${encodeURIComponent(userKey)}` : '',
    callFilter ? `&only=${callFilter}` : '',
   ].join('')
   const [o, u, c, k] = await Promise.all([
    get<Overview>(`/api/admin/spend/overview?window=${window}`),
    get<{ users: UserRow[] }>(`/api/admin/spend/users?window=${window}&limit=50`),
    get<{ chats: ChatRow[] }>(`/api/admin/spend/chats?window=${window}&limit=50${chatQuery}`),
    get<{ calls: CallRow[] }>(`/api/admin/spend/calls?window=${window}&limit=200${callQuery}`),
   ])
   setOverview(o)
   setUsers(u.users || [])
   setChats(c.chats || [])
   setCalls(k.calls || [])
  } catch (e) {
   setError(e instanceof Error ? e.message : String(e))
  } finally {
   setLoading(false)
  }
 }, [window, userKey, chatId, callFilter])

 useEffect(() => { load() }, [load])

 const total = overview?.total

 if (error) {
  return (
   <Card className="bg-primary/50 border-soft/50">
    <CardContent className="p-6">
     <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-danger mt-0.5 shrink-0" />
      <div>
       <p className="text-primary font-medium">Spend figures unavailable</p>
       <p className="text-muted text-sm mt-1">{error}</p>
       <p className="text-muted text-sm mt-2">
        This is not the same as there being no spend. The ledger lives in the newage
        database; if it is unreachable, calls are still being made and billed.
       </p>
       <Button onClick={load} className="mt-3" size="sm">
        <RefreshCw className="w-4 h-4 mr-2" /> Retry
       </Button>
      </div>
     </div>
    </CardContent>
   </Card>
  )
 }

 return (
  <div className="space-y-4">
   {/* Window selector */}
   <div className="flex items-center justify-between flex-wrap gap-2">
    <div className="flex items-center gap-1">
     {WINDOWS.map(w => (
      <Button
       key={w}
       size="sm"
       variant={window === w ? 'default' : 'outline'}
       onClick={() => setWindow(w)}
      >
       {w}
      </Button>
     ))}
    </div>
    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
     <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
     Refresh
    </Button>
   </div>

   {/* Headline. Capture and fallback come first deliberately — they qualify
       every other number on the page. */}
   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    <Stat
     label="Total spend"
     value={total ? money(total.micros) : '—'}
     sub={total ? `${num(total.calls)} calls, ${num(total.attempts)} attempts` : ''}
     icon={<DollarSign className="w-8 h-8 text-warn opacity-50" />}
    />
    <Stat
     label="Capture integrity"
     value={total ? pct(total.capture) : '—'}
     sub={
      total && total.unpriced > 0
       ? `${num(total.unpriced)} attempts have no known price`
       : 'every call priced'
     }
     tone={total && total.capture < 0.99 ? 'warn' : 'ok'}
     icon={<ShieldAlert className={`w-8 h-8 opacity-50 ${total && total.capture < 0.99 ? 'text-danger' : 'text-ok'}`} />}
    />
    <Stat
     label="Not served by primary"
     value={total ? pct(total.non_primary_share) : '—'}
     sub={
      total && total.skipped > 0
       ? `${num(total.skipped)} skipped by the breaker`
       : 'pool healthy'
     }
     tone={total && total.non_primary_share > 0.1 ? 'warn' : 'ok'}
     icon={<GitBranch className={`w-8 h-8 opacity-50 ${total && total.non_primary_share > 0.1 ? 'text-warn' : 'text-ok'}`} />}
    />
    <Stat
     label={`Avg per ${BRAND.assistant} AI turn`}
     value={total ? money(total.micros_per_turn ?? 0) : '—'}
     sub={
      total
       ? `${num(total.turns ?? 0)} turns · ${money(total.micros_per_chat)}/chat · ${money(total.micros_per_call, 6)}/call`
       : ''
     }
     icon={<Activity className="w-8 h-8 text-brand opacity-50" />}
    />
   </div>

   {/* The two warnings worth stating in prose rather than as a number. */}
   {total && total.capture < 1 && (
    <Note tone="warn">
     {num(total.unpriced)} of {num(total.attempts)} attempts have no price for the
     model that served them, so the totals on this page are lower bounds. Add the
     model to <code className="text-xs">src/llm_gateway/pricing.py</code> and re-export,
     or use the Unpriced filter below to see which calls they are.
    </Note>
   )}
   {total && total.non_primary_share > 0.1 && (
    <Note tone="warn">
     {pct(total.non_primary_share)} of attempts were not served by the first provider
     in their pool. A few percent is the pool absorbing transient rate limits;
     sustained double digits means a primary is unhealthy and spend has moved to a
     provider that may cost several times more.
    </Note>
   )}
   {total && total.legacy > 0 && (
    <Note tone="muted">
     {num(total.legacy)} attempts predate the gateway. Their provider and model
     attribution comes from the old per-runtime telemetry and is less reliable.
    </Note>
   )}

   {/* Pool health. Sits above the spend breakdowns because a pool that cannot
       serve explains a fallback share better than any cost figure can, and
       because the saved config gives no hint when a model has been retired. */}
   <Card className="bg-primary/50 border-soft/50">
    <CardHeader>
     <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
       <CardTitle className="text-primary">Model pools</CardTitle>
       <CardDescription>
        Asks every model in every pool to actually answer. The saved
        configuration cannot tell you that a provider has retired a model, that
        a model id only exists on a price page, or that an account is out of
        credit — all three were true at once, and every health check still read
        green. Costs a fraction of a cent.
       </CardDescription>
      </div>
      <Button size="sm" variant="outline" onClick={runPreflight} disabled={preflightBusy}>
       <RefreshCw className={`w-4 h-4 mr-2 ${preflightBusy ? 'animate-spin' : ''}`} />
       {preflightBusy ? 'Checking…' : preflight ? 'Re-check' : 'Check pools'}
      </Button>
     </div>
    </CardHeader>
    <CardContent>
     {preflightError && (
      <Note tone="warn">Pool check failed: {preflightError}</Note>
     )}
     {!preflight && !preflightError && (
      <p className="text-muted text-sm">
       Not checked yet. This is the check that distinguishes "configured" from
       "working".
      </p>
     )}
     {preflight && (
      <div className="space-y-4">
       {preflight.dead_classes.length > 0 && (
        <Note tone="warn">
         {preflight.dead_classes.join(', ')} cannot serve at all — every model in
         the pool failed. Any caller asking for {preflight.dead_classes.length > 1 ? 'these classes' : 'this class'} is
         getting a failed turn right now.
        </Note>
       )}
       {preflight.classes_on_fallback.length > 0 && (
        <Note tone="warn">
         {preflight.classes_on_fallback.join(', ')} {preflight.classes_on_fallback.length > 1 ? 'are' : 'is'} serving,
         but not from the first model in the pool. That is the price and latency
         of every turn on {preflight.classes_on_fallback.length > 1 ? 'those classes' : 'that class'} changing
         quietly.
        </Note>
       )}
       {preflight.classes_without_spare.length > 0 && (
        <Note tone="muted">
         {preflight.classes_without_spare.join(', ')} {preflight.classes_without_spare.length > 1 ? 'have' : 'has'} exactly
         one working model. One vendor announcement away from an outage.
        </Note>
       )}

       {preflight.classes.map(c => (
        <div key={c.class}>
         <div className="flex items-center gap-2 mb-1">
          <span className="text-primary font-medium">{c.class}</span>
          <Badge variant={c.ok ? (c.primary_ok ? 'success' : 'secondary') : 'destructive'}>
           {c.serving}/{c.entries.length} serving
          </Badge>
          {c.ok && !c.primary_ok && (
           <span className="text-warn text-xs">primary down</span>
          )}
         </div>
         <Table>
          <TableBody>
           {c.entries.map((e, i) => (
            <TableRow key={e.ref}>
             <TableCell className="text-muted w-10 text-xs">
              {/* The index is the position in the fallback chain, which is what
                  decides whether a failure here is felt by users. */}
              {i === 0 ? 'primary' : `#${i + 1}`}
             </TableCell>
             <TableCell className="font-mono text-xs text-primary">{e.ref}</TableCell>
             <TableCell className="text-right w-24">
              {e.ok ? (
               <span className="text-ok text-xs">
                {e.latency_ms != null ? `${e.latency_ms} ms` : 'ok'}
               </span>
              ) : (
               <Badge variant="destructive">{e.error_kind || 'failed'}</Badge>
              )}
             </TableCell>
             <TableCell className="text-muted text-xs">
              {e.ok
               ? (e.priced ? '' : 'no price — spend will read as $0')
               : (e.error || '').slice(0, 120)}
             </TableCell>
            </TableRow>
           ))}
          </TableBody>
         </Table>
        </div>
       ))}
       <p className="text-muted text-xs">Checked {shortTs(preflight.checked_at)}</p>
      </div>
     )}
    </CardContent>
   </Card>

   {/* Spend by provider — the breakdown that answers "where did the money go". */}
   <Card className="bg-primary/50 border-soft/50">
    <CardHeader>
     <CardTitle className="text-primary">Spend by provider</CardTitle>
     <CardDescription>
      Groq is the intended primary on price and speed. Spend concentrated elsewhere
      means the pools are falling through.
     </CardDescription>
    </CardHeader>
    <CardContent>
     <Breakdown
      rows={overview?.by_provider || []}
      keyOf={r => r.provider}
      label="Provider"
      totalMicros={total?.micros || 0}
     />
    </CardContent>
   </Card>

   <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <Card className="bg-primary/50 border-soft/50">
     <CardHeader>
      <CardTitle className="text-primary">Spend by purpose</CardTitle>
      <CardDescription>Which pass or service asked for the call.</CardDescription>
     </CardHeader>
     <CardContent>
      <Breakdown
       rows={overview?.by_purpose || []}
       keyOf={r => r.purpose}
       label="Purpose"
       totalMicros={total?.micros || 0}
      />
     </CardContent>
    </Card>

    <Card className="bg-primary/50 border-soft/50">
     <CardHeader>
      <CardTitle className="text-primary">Spend by model class</CardTitle>
      <CardDescription>
       What the gateway resolved the request to. A class costing more than expected
       is usually a pool whose first entry is failing.
      </CardDescription>
     </CardHeader>
     <CardContent>
      <Breakdown
       rows={overview?.by_class || []}
       keyOf={r => r.model_class}
       label="Class"
       totalMicros={total?.micros || 0}
      />
     </CardContent>
    </Card>
   </div>

   <Card className="bg-primary/50 border-soft/50">
    <CardHeader>
     <CardTitle className="text-primary">Spend by model</CardTitle>
     <CardDescription>Provider and model as actually served.</CardDescription>
    </CardHeader>
    <CardContent>
     <Breakdown
      rows={overview?.by_model || []}
      keyOf={r => r.model}
      label="Model"
      totalMicros={total?.micros || 0}
     />
    </CardContent>
   </Card>

   {/* Per user */}
   <Card className="bg-primary/50 border-soft/50">
    <CardHeader>
     <CardTitle className="text-primary flex items-center gap-2">
      <Users className="w-4 h-4" /> Spend by user
     </CardTitle>
     <CardDescription>
      {overview
       ? `${num(overview.users)} users made a call in this window. Averages are over those users, not all registered ones.`
       : ''}
     </CardDescription>
    </CardHeader>
    <CardContent className="overflow-x-auto">
     <Table>
      <TableHeader>
       <TableRow>
        <TableHead>User</TableHead>
        <TableHead className="text-right">Calls</TableHead>
        <TableHead className="text-right">Chats</TableHead>
        <TableHead className="text-right">Spend</TableHead>
        <TableHead className="text-right">Per chat</TableHead>
        <TableHead className="text-right">Failed</TableHead>
        <TableHead />
       </TableRow>
      </TableHeader>
      <TableBody>
       {users.length === 0 && <Empty cols={7} loading={loading} />}
       {users.map(u => (
        <TableRow key={u.user_key} className={userKey === u.user_key ? 'bg-soft/30' : ''}>
         <TableCell className="font-mono text-xs">
          {u.saas_user_id ?? u.user_key}
         </TableCell>
         <TableCell className="text-right">{num(u.calls)}</TableCell>
         <TableCell className="text-right">{num(u.chats)}</TableCell>
         <TableCell className="text-right">{moneyOf(u)}</TableCell>
         <TableCell className="text-right">{money(u.micros_per_chat)}</TableCell>
         <TableCell className="text-right">
          {u.failed > 0 ? <Badge variant="destructive">{num(u.failed)}</Badge> : '—'}
         </TableCell>
         <TableCell className="text-right">
          <Button
           size="sm"
           variant="outline"
           onClick={() => {
            setUserKey(userKey === u.user_key ? null : u.user_key)
            setChatId(null)
           }}
          >
           {userKey === u.user_key ? 'Clear' : <>Chats <ChevronRight className="w-3 h-3 ml-1" /></>}
          </Button>
         </TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
    </CardContent>
   </Card>

   {/* Per chat */}
   <Card className="bg-primary/50 border-soft/50">
    <CardHeader>
     <CardTitle className="text-primary flex items-center gap-2">
      <MessageSquare className="w-4 h-4" /> Spend by chat
     </CardTitle>
     <CardDescription className="flex items-center gap-2">
      {userKey ? (
       <>
        <span>Scoped to user {userKey}</span>
        <Button size="sm" variant="outline" onClick={() => { setUserKey(null); setChatId(null) }}>
         <ArrowLeft className="w-3 h-3 mr-1" /> All users
        </Button>
       </>
      ) : (
       <span>Dearest first. A chat is one conversation; a turn is one exchange in it.</span>
      )}
     </CardDescription>
    </CardHeader>
    <CardContent className="overflow-x-auto">
     <Table>
      <TableHeader>
       <TableRow>
        <TableHead>Chat</TableHead>
        <TableHead>User</TableHead>
        <TableHead className="text-right">Turns</TableHead>
        <TableHead className="text-right">Calls</TableHead>
        <TableHead className="text-right">Spend</TableHead>
        <TableHead className="text-right">Per turn</TableHead>
        <TableHead>Last call</TableHead>
        <TableHead />
       </TableRow>
      </TableHeader>
      <TableBody>
       {chats.length === 0 && <Empty cols={8} loading={loading} />}
       {chats.map(c => (
        <TableRow key={c.chat_id} className={chatId === c.chat_id ? 'bg-soft/30' : ''}>
         <TableCell className="font-mono text-xs" title={c.chat_id}>
          {c.chat_id.slice(0, 8)}
         </TableCell>
         <TableCell className="font-mono text-xs">{c.user_key ?? '—'}</TableCell>
         <TableCell className="text-right">{num(c.turns)}</TableCell>
         <TableCell className="text-right">{num(c.calls)}</TableCell>
         <TableCell className="text-right">{moneyOf(c)}</TableCell>
         <TableCell className="text-right">{money(c.micros_per_turn)}</TableCell>
         <TableCell className="text-muted text-xs">{shortTs(c.last_call_at)}</TableCell>
         <TableCell className="text-right">
          <Button
           size="sm"
           variant="outline"
           onClick={() => setChatId(chatId === c.chat_id ? null : c.chat_id)}
          >
           {chatId === c.chat_id ? 'Clear' : <>Calls <ChevronRight className="w-3 h-3 ml-1" /></>}
          </Button>
         </TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
    </CardContent>
   </Card>

   {/* Per call */}
   <Card className="bg-primary/50 border-soft/50">
    <CardHeader>
     <CardTitle className="text-primary">Calls</CardTitle>
     <CardDescription>
      One row per provider attempt. Attempts of the same call share a colour and
      sort together, so a failover reads as what it was: the primary refused, the
      next provider in the pool served it.
     </CardDescription>
     <div className="flex items-center gap-1 pt-2 flex-wrap">
      {([
       ['', 'All'],
       ['fallback', 'Fell back'],
       ['unpriced', 'Unpriced'],
       ['failed', 'Failed'],
      ] as const).map(([value, label]) => (
       <Button
        key={value || 'all'}
        size="sm"
        variant={callFilter === value ? 'default' : 'outline'}
        onClick={() => setCallFilter(value)}
       >
        {label}
       </Button>
      ))}
      {chatId && (
       <Button size="sm" variant="outline" onClick={() => setChatId(null)}>
        <ArrowLeft className="w-3 h-3 mr-1" /> All chats
       </Button>
      )}
     </div>
    </CardHeader>
    <CardContent className="overflow-x-auto">
     <Table>
      <TableHeader>
       <TableRow>
        <TableHead>When</TableHead>
        <TableHead>Purpose</TableHead>
        <TableHead>Class</TableHead>
        <TableHead>Served by</TableHead>
        <TableHead className="text-right">Try</TableHead>
        <TableHead className="text-right">Tokens</TableHead>
        <TableHead className="text-right">Latency</TableHead>
        <TableHead className="text-right">Cost</TableHead>
        <TableHead>Status</TableHead>
       </TableRow>
      </TableHeader>
      <TableBody>
       {calls.length === 0 && <Empty cols={9} loading={loading} />}
       {calls.map(c => (
        <TableRow key={c.id}>
         <TableCell className="text-muted text-xs whitespace-nowrap">
          {shortTs(c.request_ts)}
         </TableCell>
         <TableCell className="text-xs">{c.purpose ?? '—'}</TableCell>
         <TableCell className="text-xs">
          {c.class_resolved ?? '—'}
          {c.class_requested && c.class_requested !== c.class_resolved && (
           <span className="text-muted" title="requested class differed">
            {' '}(asked {c.class_requested})
           </span>
          )}
         </TableCell>
         <TableCell className="text-xs font-mono">
          {c.provider ? `${c.provider}/${c.model}` : '—'}
         </TableCell>
         <TableCell className="text-right">
          {/* Attempt 0 is the primary. Anything above it is a fallback and is
              worth seeing at a glance. */}
          {c.attempt_index === null ? (
           <span className="text-muted" title="pre-gateway row">—</span>
          ) : c.attempt_index > 0 ? (
           <Badge variant="secondary" title="not the primary provider">
            #{c.attempt_index + 1}
           </Badge>
          ) : (
           <span className="text-muted">1</span>
          )}
         </TableCell>
         <TableCell className="text-right text-xs whitespace-nowrap">
          {c.prompt_tokens === null && c.completion_tokens === null ? (
           <span className="text-warn" title="no usage block returned, but the provider still billed">
            none
           </span>
          ) : (
           <>
            {num(c.prompt_tokens || 0)}
            {(c.cached_prompt_tokens || 0) > 0 && (
             <span className="text-ok" title={`${num(c.cached_prompt_tokens || 0)} served from cache`}>
              {' '}({num(c.cached_prompt_tokens || 0)}c)
             </span>
            )}
            {' → '}
            {num(c.completion_tokens || 0)}
           </>
          )}
         </TableCell>
         <TableCell className="text-right text-xs">
          {c.latency_ms === null ? '—' : `${num(c.latency_ms)}ms`}
         </TableCell>
         <TableCell className="text-right text-xs whitespace-nowrap">
          {c.priced === false || c.cost_usd_micros === null ? (
           <span className="text-warn" title="no price for this provider and model">
            unknown
           </span>
          ) : (
           money(c.cost_usd_micros, 6)
          )}
         </TableCell>
         <TableCell className="text-xs">
          {c.skipped_reason ? (
           <Badge variant="secondary" title={c.skipped_reason}>skipped</Badge>
          ) : c.status === 'ok' ? (
           <span className="text-ok">ok</span>
          ) : (
           <Badge variant="destructive" title={c.error_message || c.error_kind || ''}>
            {c.error_kind || c.http_status || 'failed'}
           </Badge>
          )}
         </TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
    </CardContent>
   </Card>
  </div>
 )
}

// ── Pieces ────────────────────────────────────────────────────

function Stat({ label, value, sub, icon, tone }: {
 label: string
 value: string
 sub?: string
 icon: React.ReactNode
 tone?: 'ok' | 'warn'
}) {
 return (
  <Card className="bg-primary/50 border-soft/50">
   <CardContent className="p-4">
    <div className="flex items-center justify-between">
     <div className="min-w-0">
      <p className="text-muted text-sm">{label}</p>
      <p className={`text-2xl font-bold ${tone === 'warn' ? 'text-warn' : 'text-primary'}`}>
       {value}
      </p>
      {sub && <p className="text-muted text-xs mt-1 truncate" title={sub}>{sub}</p>}
     </div>
     {icon}
    </div>
   </CardContent>
  </Card>
 )
}

function Note({ tone, children }: { tone: 'warn' | 'muted'; children: React.ReactNode }) {
 return (
  <div
   className={`rounded-md border p-3 text-sm ${
    tone === 'warn'
     ? 'border-warn/40 bg-warn/10 text-primary'
     : 'border-soft/50 bg-primary/30 text-muted'
   }`}
  >
   {children}
  </div>
 )
}

/**
 * A one-dimension breakdown table.
 *
 * Shares one shape across provider, purpose, class and model so the four read
 * the same way, and so `unpriced` appears on every one of them — a breakdown
 * that hides its unpriced count is exactly how a partial total gets believed.
 */
function Breakdown<T extends Measures>({ rows, keyOf, label, totalMicros }: {
 rows: T[]
 keyOf: (row: T) => string
 label: string
 totalMicros: number
}) {
 return (
  <div className="overflow-x-auto">
   <Table>
    <TableHeader>
     <TableRow>
      <TableHead>{label}</TableHead>
      <TableHead className="text-right">Calls</TableHead>
      <TableHead className="text-right">Spend</TableHead>
      <TableHead className="text-right">Share</TableHead>
      <TableHead className="text-right">Unpriced</TableHead>
      <TableHead className="text-right">p95</TableHead>
     </TableRow>
    </TableHeader>
    <TableBody>
     {rows.length === 0 && <Empty cols={6} loading={false} />}
     {rows.map(r => (
      <TableRow key={keyOf(r)}>
       <TableCell className="font-mono text-xs">{keyOf(r)}</TableCell>
       <TableCell className="text-right">{num(r.calls)}</TableCell>
       <TableCell className="text-right">{moneyOf(r)}</TableCell>
       <TableCell className="text-right text-muted text-xs">
        {totalMicros > 0 ? pct(r.micros / totalMicros) : '—'}
       </TableCell>
       <TableCell className="text-right">
        {r.unpriced > 0 ? <Badge variant="secondary">{num(r.unpriced)}</Badge> : '—'}
       </TableCell>
       <TableCell className="text-right text-muted text-xs">{num(r.p95_ms)}ms</TableCell>
      </TableRow>
     ))}
    </TableBody>
   </Table>
  </div>
 )
}

function Empty({ cols, loading }: { cols: number; loading: boolean }) {
 return (
  <TableRow>
   <TableCell colSpan={cols} className="text-center text-muted py-6">
    {loading ? 'Loading…' : 'No calls in this window.'}
   </TableCell>
  </TableRow>
 )
}
